import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { isAdmin, getCurrentUserId } from '@utils';
/* eslint-disable react/no-unescaped-entities */
import { ReactComponent as CloseIcon } from '@assets/icons/x_close_icon.svg';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-ignore
import {
  InspectionDriverLicenseSection,
  InspectionVehicleDetailsSection,
  InspectionRegoPapersSection,
  InspectionBankSection,
  InspectionRoadworthyCertSection,
  InspectionPayoutLetterSection,
  InspectionOwnershipProofSection,
  InspectionDocumentStatusEnum
} from '@types';
import DriverLicense from '@pages/Inspections/components/CheckInfo/DriverLicense';
import VehicleDetails from '@pages/Inspections/components/CheckInfo/VehicleDetails';
import RegoPaper from '@pages/Inspections/components/CheckInfo/RegoPaper';
import BankStatement from '@pages/Inspections/components/CheckInfo/BankStatement';
import Cert from '@pages/Inspections/components/CheckInfo/Cert';
import ActivityHistory from '@pages/Inspections/components/ActivityHistory';
import { useInspectionDetailStore } from '@stores';
import StatusChip from '../../../../components/widgets/StatusChip';
import { useMediaQuery } from 'usehooks-ts';
import ApprovalRequest from '../ApprovalRequest';
import { useParams } from 'react-router-dom';
import { UPDATE_INSPECTION_RULE, GET_INSPECTION_CHECK, GET_INSPECTION, post } from '@services';
import { Button } from '@material-tailwind/react';
import { panelToRuleMap } from '../../utils/ruleMappings';
import { isInspectionBlockedOrApprovalRequired } from '@utils/inspectionUtils';
import {
  isFailedInspectionRule,
  getRuleTypesForSecurityCheck
} from '../../utils/securityCheckHandlers';
import { INSPECTION_DOCUMENT_STATUS_VARIANT, InspectionStatusLabelEnum } from '@constants';

export const RightDetailsBar = () => {
  const params = useParams();
  const {
    activeStep,
    isCollapsed,
    showActivity,
    activePanel,
    setCollapse,
    variant,
    statusChipLabel,
    inspectionDetailData,
    setInspectionChecks,
    setInspectionDetailData
  } = useInspectionDetailStore();

  // Get current user role (lender or broker)
  const getCurrentUserRole = (): 'lender' | 'broker' => {
    const userId = localStorage.getItem('userId');
    const lenderUser = localStorage.getItem(`lenders_${userId}`);

    try {
      return lenderUser && JSON.parse(lenderUser).length > 0
        ? 'lender'
        : 'broker';
    } catch {
      return 'broker';
    }
  };

  // Helper function to find matching rule for vehicle photos
  const findVehiclePhotoRule = (failedRules: any[], activeSection: string) => {
    if (activeSection === 'vehiclePhotosQuality') {
      return failedRules.find(
        (rule: any) => rule.ruleType === 'ASSET_PHOTOS_QUALITY'
      );
    } else if (activeSection === 'vehiclePhotosAuthenticity') {
      return failedRules.find(
        (rule: any) => rule.ruleType === 'ASSET_PHOTOS_AUTHENTICITY'
      );
    } else {
      // Backward compatibility for vehiclePhotos
      return failedRules.find(
        (rule: any) =>
          rule.ruleType === 'ASSET_PHOTOS_AUTHENTICITY' ||
          rule.ruleType === 'ASSET_PHOTOS_QUALITY'
      );
    }
  };

  // Check if current user can view ApprovalRequest component
  const canViewApprovalRequest = () => {
    // Check if user is admin
    if(isAdmin()) {
      return true;
    }

    const currentUserId = getCurrentUserId();
    // Must be the creator
    if (inspectionDetailData.creator?.userId === currentUserId) {
      return true;
    }

    return false;
  };

  const docTypes = [
    InspectionDriverLicenseSection,
    InspectionVehicleDetailsSection,
    InspectionRegoPapersSection,
    InspectionBankSection,
    InspectionRoadworthyCertSection,
    InspectionPayoutLetterSection,
    InspectionOwnershipProofSection
  ];
  const isMobile = useMediaQuery('(max-width: 720px)');
  const currentSection = docTypes[parseInt(activeStep, 10) - 1];
  const [content, setContent] = useState<JSX.Element | null>(null);

  useEffect(() => {
    const renderContent = () => {
      switch (activeStep) {
        case '1':
          return <DriverLicense />;
        case '2':
          return <VehicleDetails />;
        case '3':
          return <RegoPaper />;
        case '4':
          return <BankStatement />;
        case '5':
          return <Cert />;
        case '6':
          return <Cert />;
        case '7':
          return <Cert />;
        default:
          return null;
      }
    };

    setContent(renderContent());
  }, [activeStep]);

  const handleClose = () => {
    setCollapse(true);
  };

  const findMatchingFailedRule = () => {
    const rules = inspectionDetailData?.inspectionChecks?.rules;
    if (!rules?.length) return null;

    const failedRules = rules.filter(isFailedInspectionRule);
    if (!failedRules.length) return null;

    const ruleType = panelToRuleMap[activePanel.activeSection];
    const candidateRuleTypes = Array.from(
      new Set([
        ruleType,
        ...getRuleTypesForSecurityCheck(undefined, activePanel.activeSection)
      ].filter(Boolean))
    );
    if (!candidateRuleTypes.length) return null;

    let matchingRule = failedRules.find((rule: any) =>
      candidateRuleTypes.includes(rule.ruleType)
    );

    if (
      !matchingRule &&
      (activePanel.activeSection === 'vehiclePhotos' ||
        activePanel.activeSection === 'vehiclePhotosQuality' ||
        activePanel.activeSection === 'vehiclePhotosAuthenticity')
    ) {
      matchingRule = findVehiclePhotoRule(
        failedRules,
        activePanel.activeSection
      );
    }

    return matchingRule || null;
  };

  // Get current rule type for the active panel
  const getCurrentRuleType = () => findMatchingFailedRule();

  // Handle approval request submission
  const handleApprovalSubmit = async (data: {
    comment: string;
    action: 'comment' | 'approve';
    hasException?: boolean;
    isApproved?: boolean;
  }) => {
    const currentRule = getCurrentRuleType();
    if (!currentRule) return;

    const requestData: any = {
      inspectionId: params.id,
      ruleType: currentRule.ruleType,
      comment: data.comment
    };

    // Only include hasException if provided (for broker comments)
    if (data.hasException !== undefined) {
      requestData.hasException = data.hasException;
    }

    // Only include isApproved if provided (for lender approvals)
    if (data.isApproved !== undefined) {
      requestData.isApproved = data.isApproved;
    }
    try {
      const response = await post(UPDATE_INSPECTION_RULE, requestData, false);

      if (
        response &&
        typeof response === 'object' &&
        Object.keys(response).length === 0
      ) {
        const [checkResponse, inspectionResponse] = await Promise.all([
          post(GET_INSPECTION_CHECK, { inspectionId: params.id }),
          post(GET_INSPECTION, { inspectionId: params.id }, false)
        ]);

        if (checkResponse && checkResponse.inspectionChecks) {
          const updatedChecks = {
            ...checkResponse.inspectionChecks,
            rules: checkResponse.rules || []
          };
          setInspectionChecks(updatedChecks);

          if (inspectionResponse?.inspection) {
            setInspectionDetailData({
              ...inspectionResponse.inspection,
              inspectionChecks: updatedChecks
            });
          } else {
            setInspectionDetailData({
              ...inspectionDetailData,
              inspectionChecks: updatedChecks
            });
          }

          if (data.action === 'approve') {
            setCollapse(true);
          }
        }
      }
    } catch (error: any) {
      if (error) {
        throw new Error(
          error?.response?.data?.error?.message || 'Failed to submit request'
        );
      }
    }
  };

  // Get rule status for current panel
  const getRuleStatus = () => {
    if (!isInspectionBlockedOrApprovalRequired(inspectionDetailData.status))
      return null;

    const matchingRule = findMatchingFailedRule();

    if (!matchingRule) return null;

    // Check exception status first
    if (matchingRule.exception === 'EXCEPTION_GRANTED') {
      return InspectionStatusLabelEnum.Approved;
    }
    if (
      matchingRule.exception === 'APPROVAL_REQUIRED' ||
      matchingRule.status === 'IN_REVIEW'
    ) {
      return InspectionStatusLabelEnum.InReview;
    }
    return InspectionStatusLabelEnum.CriteriaNotMet;
  };

  // Get title based on document type and active section
  const getTitle = () => {
    const inspectionChecks = inspectionDetailData?.inspectionChecks;
    const licenseEdits = inspectionChecks?.licenseEdits;
    const licenseAuthenticity = inspectionChecks?.licenseAuthenticity;
    const documentType =
      licenseEdits?.documentType || licenseAuthenticity?.documentType;

    // Override for DRIVER_LICENCE_FRONT when it's a passport
    if (
      activePanel.activeSection === 'DRIVER_LICENCE_FRONT' &&
      documentType === 'PASSPORT'
    ) {
      return 'Passport';
    }

    return currentSection?.[activePanel.activeSection] ?? '';
  };

  const showApprovalRequest = (): boolean => {
    const notCompleted = variant !== INSPECTION_DOCUMENT_STATUS_VARIANT[InspectionDocumentStatusEnum.Completed];
    return (
      notCompleted &&
      isInspectionBlockedOrApprovalRequired(inspectionDetailData.status) &&
      inspectionDetailData.inspectionConfig?.isExceptionApprovalRequired &&
      canViewApprovalRequest()
    );
  };

  const showCommentBox = (): boolean =>
    showApprovalRequest() ||
    Boolean(
      currentRuleType &&
        inspectionDetailData.inspectionConfig?.isExceptionApprovalRequired &&
        canViewApprovalRequest()
    );

  // Get the updated variant and status chip label
  const currentRuleType = getCurrentRuleType();
  let variantUpdated = variant;
  let statusChipLabelUpdated = statusChipLabel;
  if (currentRuleType?.hasException && currentRuleType?.exception === 'EXCEPTION_GRANTED') {
    variantUpdated = INSPECTION_DOCUMENT_STATUS_VARIANT[InspectionDocumentStatusEnum.Completed];
    statusChipLabelUpdated = InspectionStatusLabelEnum.Approved;
  }
  
  // Advanced approval is enabled if isExceptionApprovalRequired is true
  const isExceptionApprovalRequired = inspectionDetailData.inspectionConfig?.isExceptionApprovalRequired

  return (
    <div
      className={clsx(
        'fixed bg-opacity-0 w-screen min-h-screen flex ',
        isCollapsed ? '!w-0 hidden' : 'z-70'
      )}>
      <div
        className={clsx(
          'w-full h-full',
          isCollapsed || isMobile ? 'hidden' : 'z-80 fixed'
        )}
        onClick={handleClose}></div>
      <div
        className={clsx(
          'flex flex-col sidebarMenu fixed h-full right-0 bg-white xl:w-1/4 lg:w-1/3 md:w-1/2 w-4/5 border shadow-xl rightBar z-100 clear-both',
          isCollapsed ? 'collapsed' : 'expanded'
        )}>
        <div className="flex flex-col h-full">
          {!showActivity && (
            <div className="flex flex-wrap p-6 gap-3 items-center border-b">
              <h3 className="text-lg font-semibold text-grey-900 whitespace-nowrap">
                {activePanel.activeSection === 'vehiclePhotos' ||
                activePanel.activeSection === 'vehiclePhotosQuality' ||
                activePanel.activeSection === 'vehiclePhotosAuthenticity'
                  ? 'AI '
                  : ''}
                {getTitle()}
              </h3>
              <StatusChip
                variant={variantUpdated}
                clsName={clsx(
                  'border text-xs font-normal flex justify-center items-center whitespace-nowrap'
                )}
                label={statusChipLabelUpdated}
              />
              {(() => {
                const ruleStatus = getRuleStatus();
                if (!ruleStatus) return null;

                const isInReview = ruleStatus === InspectionStatusLabelEnum.InReview;
                const isApproved = ruleStatus === InspectionStatusLabelEnum.Approved;
                return (
                  <span
                    className={clsx(
                      'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap',
                      isInReview
                        ? 'bg-secondary-light/10 border border-secondary-light text-secondary-light'
                        : isApproved
                          ? 'bg-success text-white'
                          : 'bg-error-50 text-error-700 border border-error-300'
                    )}>
                    {ruleStatus}
                  </span>
                );
              })()}
              <div
                className={clsx(
                  'absolute right-6 cursor-pointer hover:shadow-paper hover:border'
                )}>
                <div onClick={handleClose}>
                  <CloseIcon />
                </div>
              </div>
            </div>
          )}
          <div className="flex-1 min-h-0 overflow-y-auto">
            <div className="pb-[54px]">
              {showActivity && <ActivityHistory />}
              {!showActivity && (
                <>
                  {content}
                  {isExceptionApprovalRequired && (
                    <div className="px-6">
                      <ApprovalRequest
                        onSubmit={handleApprovalSubmit}
                        userRole={getCurrentUserRole()}
                        ruleType={getCurrentRuleType()?.ruleType}
                        ruleStatus={getRuleStatus()}
                        showCommentBox={showCommentBox()}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="h-[54px] mb-[60px] flex items-center justify-center p-4">
            <Button
              className="w-full font-semibold bg-white border-grey-300 text-grey-700 text-base capitalize"
              variant="outlined"
              onClick={handleClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
export default RightDetailsBar;
